import { createContext, useContext, useEffect, useCallback, useState, ReactNode } from "react";
import { ConversationSelectors } from "redux-store";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { StorageUtil } from "utils";
import { getInteractor } from "services/local.service";
import { camelizeKeys } from "humps";
import { useMessageStyle } from "../ChatItem/ChatItem.helper";
import { FormatConstant, KeyConstant, SystemConstant } from "const";
import clsx from "clsx";
import ChatTypography from "../ChatItem/ChatTypography";
import { AppAvatar } from "components";
import { convertMillisecondsToDate } from "utils/date.utils";
import { refactorMessage } from "../ViewMode.helper";
import { JsonObject } from "types/common";

/* ------------- Context Types ------------- */
interface EditingHistoryContextProps {
  showHistory: (source_id: string) => Promise<void>;
}

/* ------------- Context ------------- */
const EditingHistoryContext = createContext<EditingHistoryContextProps | undefined>(undefined);

export const useEditingHistoryContext = () => {
  const context = useContext(EditingHistoryContext);
  if (!context) {
    throw new Error("useEditingHistoryContext must be used within EditingHistoryProvider");
  }
  return context;
};

/* ------------- Redux Selector ------------- */
const memoizedReduxState = createSelector([ConversationSelectors.getSelectedGroupId], selectedGroupId => ({
  selectedGroupId,
}));

/* ------------- Provider ------------- */
interface EditingHistoryProviderProps {
  children: ReactNode;
}
export const EditingHistoryProvider = ({ children }: EditingHistoryProviderProps) => {
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const { selectedGroupId } = useSelector(memoizedReduxState);

  const [isShowHistory, setIsShowHistory] = useState(false);
  const [messageHistories, setMessageHistories] = useState<any[]>([]);

  const showHistory = useCallback(async (source_id: string) => {
    const originMessage = await getInteractor(prefixKey).LocalMessageService.findOne({ source_id });
    const editingHistories = originMessage?.source_id
      ? await getInteractor(prefixKey).LocalMessageService.find({
          parent_id: source_id,
          send_type: [
            originMessage.send_type,
            SystemConstant.SEND_TYPE.message,
            SystemConstant.SEND_TYPE.groupMessage,
            SystemConstant.SEND_TYPE.link,
          ],
        })
      : [];

    if (originMessage?.source_id) {
      editingHistories.unshift(originMessage);
    }
    const displayMessage = await Promise.all(
      camelizeKeys(editingHistories).map((item: JsonObject) => refactorMessage(item)),
    );
    setIsShowHistory(editingHistories.length > 0);
    setMessageHistories(displayMessage);
  }, []);

  const handleCloseHistory = useCallback(() => {
    setIsShowHistory(false);
    setMessageHistories([]);
  }, []);

  useEffect(() => {
    setIsShowHistory(false);
    setMessageHistories([]);
  }, [selectedGroupId]);

  return (
    <EditingHistoryContext.Provider value={{ showHistory }}>
      {children}
      {isShowHistory && (
        <Box sx={{ position: "absolute", left: 0, top: 0, width: "100%", height: "100%", zIndex: 10 }}>
          <Stack
            component={Paper}
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: 5,
              background: "white",
              padding: 2.75,
            }}
            elevation={0}
          >
            <Stack gap={2} flexDirection="row">
              <IconButton onClick={handleCloseHistory}>
                <ArrowBack sx={{ color: "black" }} />
              </IconButton>

              <Stack flexDirection="column" flexGrow={1} gap={0.5}>
                <Typography variant="subtitle1" fontSize={15}>
                  Lịch sử
                </Typography>
                <Typography variant="subtitle1" fontSize={12} color="textSecondary">
                  {messageHistories.length - 1} lần chỉnh sửa
                </Typography>
              </Stack>
            </Stack>

            <Stack flexDirection="column" flexGrow={1} padding={2} gap={2} overflow="auto">
              {messageHistories.map((history, index) => (
                <MessageHistory key={index} message={history} />
              ))}
            </Stack>
          </Stack>
        </Box>
      )}
    </EditingHistoryContext.Provider>
  );
};

export const MessageHistory = ({ message }) => {
  const messengerClasses = useMessageStyle();
  const msgCreatedTime = convertMillisecondsToDate(message.created, FormatConstant.FM_HH_MM);

  const isMine = message.senderId === StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID);

  return (
    <Stack direction={isMine ? "row-reverse" : "row"} alignItems={"flex-end"} spacing={1}>
      {!isMine && <AppAvatar avatarId={message.avatarId} size={40} />}

      <Box
        className={clsx(
          messengerClasses.messengerRoot,
          isMine ? messengerClasses.myMessage : messengerClasses.messengerRoot,
        )}
      >
        <ChatTypography
          messageContent={message.content}
          className={clsx(messengerClasses.spacing, "preview-chat")}
          messageId={message.id}
          messageSourceId={message.sourceId}
        />
      </Box>

      <Box sx={{ marginBottom: -0.5 }}>
        <Typography
          className={clsx(messengerClasses.createdTimeText, "preview-time-text")}
          sx={{ color: "#7F7F80", width: "max-content" }}
        >
          {msgCreatedTime}
        </Typography>
      </Box>
    </Stack>
  );
};
